import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SparkIcon, Text, UploadArea, useReadOnly } from '@spark';
import { DatasetOrigin, isGenerativeTask } from '@/shared/domain/taskTypes';
import {
    stripCloneSuffix,
    isSdkTraceDatasetName,
    getDataOverviewFileDisplayName,
} from '@/features/datasets/utils/datasetCloneNames';
import {
    aiGenerationProgressKey,
    collectInFlightProgressFileNames,
    fileMatchesAiGenerationDataset,
    fileShouldShowAiDatasetSparkle,
    isAiGenerationInFlightForUsecase,
    listActiveAiGenerationEntriesForUsecase,
    resolveFileTileProgressStatus,
} from '@/features/datasets/utils/datasetOverviewProgress';
import { isAiGeneratedDatasetName } from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import ObjectiveTabs from '@/features/tasks/components/Task/ObjectiveTabs';
import useUsecaseDataOverview from '@/features/datasets/hooks/useUsecaseDataOverview';
import { useUsecaseFileRemoval } from '@/features/datasets/hooks/useUsecaseFileRemoval';
import { useAuth } from '@/features/auth/AuthContext';
import { GlobalContext } from '@/app/providers/globalState';
import LanguageSection from '@/features/datasets/components/DataCollection/dataOverview/sections/LanguageSection';
import CodeSection from '@/features/datasets/components/DataCollection/dataOverview/sections/CodeSection';
import ToolsCalledSection from '@/features/datasets/components/DataCollection/dataOverview/sections/ToolsCalledSection';
import PunctuationSection from '@/features/datasets/components/DataCollection/dataOverview/sections/PunctuationSection';
import InsightsSection from '@/features/datasets/components/DataCollection/dataOverview/sections/InsightsSection';
import LabelsSection from '@/features/datasets/components/DataCollection/dataOverview/sections/LabelsSection';
import MetricsRow from '@/features/datasets/components/DataCollection/dataOverview/sections/MetricsRow';
import AddDataButton from '@/features/datasets/components/AddDataButton';
import DataOverviewUploadEmptyCard from '@/features/datasets/components/DataCollection/dataOverview/DataOverviewUploadEmptyCard';
import {
    partitionFilesBySupport,
    showUnsupportedFileTypeToast,
    unsupportedFileTypeCardMessage,
} from '@/features/datasets/utils/unsupportedFileHandling';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';
import { useTaskDataWindowEvents } from '@/features/tasks/hooks/useTaskDataWindowEvents';
import {
    datasetSourcePillLabel,
    shouldShowAiGeneratedFileCard,
    sourceFieldsFromDatasetInfoDoc,
} from '@/features/datasets/utils/datasetSourceConfig';
import DatasetSourceDetailsModal from '@/features/datasets/components/DataCollection/DatasetSourceDetails/DatasetSourceDetailsModal';
import '@/features/datasets/styles/DataOverviewCard.css';

const formatFileSize = (sum) => {
    const sizeInBytes = sum || 0;
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAiGenerated = (origin) =>
    origin === DatasetOrigin.specific_ai_fully_generated ||
    origin === DatasetOrigin.specific_ai_labeled ||
    origin === DatasetOrigin.specific_ai_agent;

// Generative tasks (summarization / content generation) output free-form text —
// there is no discrete label/entity distribution to surface, so the
// right-column "Output Detected" panel is omitted (same policy as ModelDetailsDataTab).
const showOutputPanel = (taskType) => !isGenerativeTask(taskType);

/**
 * Headless probe — calls `useUsecaseDataOverview` for one usecase and pushes
 * the result back to the parent via `onResult`. Lets us collect analysis for
 * every objective in a sub-group without violating the rules of hooks
 * (otherwise we'd be calling the hook inside a `.map`).
 *
 * `refreshKey` is forwarded to the hook so the card can force a re-fetch
 * once an in-flight upload finishes processing — the initial fetch lands
 * while data_processor is still consuming records and would otherwise leave
 * the panel stuck on "0 examples".
 *
 * Renders nothing.
 */
const UsecaseAnalysisProbe = ({ usecase, onResult, enabled = true, refreshKey = 0 }) => {
    const result = useUsecaseDataOverview(usecase, { enabled, refreshKey });
    const { data, benchmarkData, externalModelCounts, datasetsInfo, isLoading } = result;
    useEffect(() => {
        onResult(usecase._id, {
            data,
            benchmarkData,
            externalModelCounts,
            datasetsInfo,
            isLoading,
        });
    }, [usecase._id, data, benchmarkData, externalModelCounts, datasetsInfo, isLoading, onResult]);
    return null;
};

/**
 * Uploaded-file row for Data Overview — Spark UploadArea (success / in-progress).
 * AI-generated and retrieval-agent files use the AI pill card (Figma 5132:15780).
 */
const FileTile = ({
    name,
    size,
    sizeLoading = false,
    onRemove,
    onInfo,
    uploadStatus,
    aiGenerated = false,
    aiPillLabel,
    allowRemoveInProgress = false,
}) => {
    const inProgress = Boolean(uploadStatus?.inProgress);
    const state = inProgress ? 'in-progress' : 'success';
    const progress = typeof uploadStatus?.progress === 'number' ? uploadStatus.progress : 0;
    const isAiSession = isAiGeneratedDatasetName(name);
    const tileName = isAiSession ? stripCloneSuffix(name) || name : name;

    return (
        <div
            className={`fs-unmask data-overview-file-tile${isAiSession ? ' data-overview-file-tile--ai-session' : ''}${aiGenerated ? ' data-overview-file-tile--ai' : ''}`}
        >
            <UploadArea
                state={state}
                fileName={tileName}
                fileSize={formatFileSize(size)}
                fileSizeLoading={sizeLoading}
                progress={progress}
                onRemove={onRemove}
                onInfo={onInfo}
                aiGenerated={aiGenerated}
                aiPillLabel={aiPillLabel}
                allowRemoveInProgress={allowRemoveInProgress}
                className="data-overview-file-tile__upload"
            />
        </div>
    );
};

/**
 * Bordered files box (Figma 3001:68761 / 3001:68767) — "Datasets (N)" /
 * "Benchmarks (N)" header with an optional "+" action, tiles below.
 * When `onFileDrop` is set, the box accepts OS file drag-and-drop to start
 * an upload for that side (dataset vs benchmark). Drops may include multiple
 * files — the handler receives a `File[]`. Files with an unsupported suffix
 * are rejected with an inline error on the box plus a bottom-right toast.
 */
const FilesBox = ({
    title,
    count,
    action,
    children,
    onFileDrop,
    taskType,
    isAgentic = false,
    dropDisabled = false,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [dropError, setDropError] = useState(null);
    const dragDepthRef = useRef(0);
    const droppable = typeof onFileDrop === 'function' && !dropDisabled;

    const resetDrag = () => {
        dragDepthRef.current = 0;
        setIsDragOver(false);
    };

    const handleDragEnter = (e) => {
        if (!droppable) return;
        const types = e.dataTransfer?.types;
        if (!types || !Array.from(types).includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragOver(true);
    };

    const handleDragOver = (e) => {
        if (!droppable) return;
        const types = e.dataTransfer?.types;
        if (!types || !Array.from(types).includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (e) => {
        if (!droppable) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragOver(false);
    };

    const handleDrop = (e) => {
        if (!droppable) return;
        e.preventDefault();
        e.stopPropagation();
        resetDrag();
        const files = Array.from(e.dataTransfer?.files || []).filter(Boolean);
        if (!files.length) return;
        const formatOptions = { isAgentic };
        const { supported, unsupported } = partitionFilesBySupport(files, taskType, formatOptions);
        setDropError(
            unsupported.length ? unsupportedFileTypeCardMessage(taskType, formatOptions) : null
        );
        if (unsupported.length) showUnsupportedFileTypeToast(unsupported, taskType, formatOptions);
        if (supported.length) onFileDrop(supported);
    };

    const className = [
        'data-overview-box',
        'data-overview-files-box',
        droppable ? 'data-overview-files-box--droppable' : '',
        isDragOver ? 'data-overview-files-box--drag-over' : '',
        dropError ? 'data-overview-files-box--error' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={className}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="data-overview-files-box__header">
                <Text as="p" variant="lead-medium" color="primary" className="fs-unmask data-overview-files-box__title">
                    {title} ({count})
                </Text>
                {action ? <div className="data-overview-files-box__header-actions">{action}</div> : null}
            </div>
            {children}
            {dropError && (
                <div className="data-overview-files-box__error" role="alert">
                    <span>{dropError}</span>
                    <button
                        type="button"
                        className="data-overview-upload-error-dismiss"
                        aria-label="Dismiss error"
                        onClick={() => setDropError(null)}
                    >
                        <SparkIcon slug="close" size="small" />
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * Files row — Datasets and Benchmarks boxes side by side (Figma 3001:68760),
 * plus an External Files box (manual model predictions) only when such files
 * exist. Each box scrolls its tiles horizontally so the row stays compact.
 *
 * `showDatasets` / `showBenchmarks` hide a side (used when the other column
 * is a tall upload-empty card).
 */
const FilesBoxesRow = ({
    files,
    onRemoveFile,
    getUploadStatus,
    progressStatus,
    addDataUsecase,
    showDatasets = true,
    showBenchmarks = true,
}) => {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const { dispatchTaskDataEvent } = useTaskDataWindowEvents();
    const dropDisabled = readOnly || role === 'Viewer' || !addDataUsecase;
    // Track the open details tile by key (not a snapshot of the file object)
    // so the modal picks up `sourceConfig` the moment the analysis probe
    // resolves — the user may open it while the card is still loading.
    const [detailsFileKey, setDetailsFileKey] = useState(null);
    const detailsFile = useMemo(
        () =>
            detailsFileKey
                ? files.find((f) => `${f.kind}:${f.name}` === detailsFileKey) || null
                : null,
        [files, detailsFileKey]
    );

    const openUploadWithFiles = useCallback(
        (files, uploadTarget) => {
            const fileList = Array.from(files || []).filter(Boolean);
            if (!addDataUsecase || fileList.length === 0) return;
            dispatchTaskDataEvent(
                'task:add-data',
                {
                    source: 'upload',
                    usecaseId: addDataUsecase._id,
                    uploadTarget,
                    files: fileList,
                    // Back-compat for listeners that still read a single file.
                    file: fileList[0],
                },
                addDataUsecase
            );
        },
        [addDataUsecase, dispatchTaskDataEvent]
    );

    const datasets = files.filter((f) => f.kind === 'dataset');
    const benchmarks = files.filter((f) => f.kind === 'benchmark');
    const externals = files.filter((f) => f.kind === 'manual');

    const filesBoxAction = (uploadTarget) => {
        if (!addDataUsecase) return null;
        return (
            <AddDataButton
                usecase={addDataUsecase}
                iconOnly
                uploadTarget={uploadTarget}
                placement="bottomRight"
            />
        );
    };

    const renderTiles = (list, withRemove) => {
        if (!list.length) {
            return (
                <Text
                    as="p"
                    variant="detail-regular"
                    color="tertiary"
                    className="fs-unmask data-overview-files-box__empty"
                >
                    Nothing here yet.
                </Text>
            );
        }
        return (
            <div className="optune-scrollbar data-overview-files-box__tiles">
                {list.map((file) => {
                    const showAiSparkle = fileShouldShowAiDatasetSparkle(
                        file,
                        progressStatus,
                        file.origin,
                        isAiGenerated
                    );
                    const aiGenerated = shouldShowAiGeneratedFileCard({
                        origin: file.origin,
                        sourceConfig: file.sourceConfig,
                        datasetName: file.name,
                    });
                    return (
                        <FileTile
                            key={`${file.kind}:${file.name}`}
                            name={file.name}
                            size={file.size}
                            sizeLoading={Boolean(file.sizeLoading) && !file.size}
                            onRemove={
                                withRemove && onRemoveFile ? () => onRemoveFile(file) : undefined
                            }
                            onInfo={
                                aiGenerated
                                    ? () => setDetailsFileKey(`${file.kind}:${file.name}`)
                                    : undefined
                            }
                            uploadStatus={getUploadStatus?.(file) || null}
                            aiGenerated={aiGenerated}
                            aiPillLabel={datasetSourcePillLabel({
                                origin: file.origin,
                                sourceConfig: file.sourceConfig,
                                datasetName: file.name,
                            })}
                            allowRemoveInProgress={showAiSparkle}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className="data-overview-boxes-row">
            {showDatasets && (
                <FilesBox
                    title="Datasets"
                    count={datasets.length}
                    taskType={addDataUsecase?.task_type}
                    isAgentic={isAgenticAutonomyLevel(addDataUsecase?.autonomy_level)}
                    dropDisabled={dropDisabled}
                    onFileDrop={(files) => openUploadWithFiles(files, 'dataset')}
                    action={filesBoxAction('dataset')}
                >
                    {renderTiles(datasets, true)}
                </FilesBox>
            )}
            {showBenchmarks && (
                <FilesBox
                    title="Benchmarks"
                    count={benchmarks.length}
                    taskType={addDataUsecase?.task_type}
                    isAgentic={isAgenticAutonomyLevel(addDataUsecase?.autonomy_level)}
                    dropDisabled={dropDisabled}
                    onFileDrop={(files) => openUploadWithFiles(files, 'benchmark')}
                    action={filesBoxAction('benchmark')}
                >
                    {renderTiles(benchmarks, true)}
                </FilesBox>
            )}
            {externals.length > 0 && (
                <FilesBox title="External Files" count={externals.length}>
                    {renderTiles(externals, false)}
                </FilesBox>
            )}
            <DatasetSourceDetailsModal
                open={Boolean(detailsFile)}
                onClose={() => setDetailsFileKey(null)}
                origin={detailsFile?.origin}
                sourceConfig={detailsFile?.sourceConfig}
                createdByUser={detailsFile?.createdByUser}
                createdDatetime={detailsFile?.createdDatetime}
                datasetName={detailsFile?.name}
                configLoading={Boolean(detailsFile?.sourceLoading)}
            />
        </div>
    );
};


const LoadingState = () => (
    <div
        style={{
            padding: 10,
            textAlign: 'center',
            marginTop: 60,
            marginBottom: 60,
        }}
    >
        <div
            className="data-overview-loading-spinner"
            role="status"
            aria-label="Analysing data"
            style={{
                width: 36,
                height: 36,
                margin: '0 auto',
                borderRadius: '50%',
                border: '3px solid var(--semantic-feedback-info-surface-muted)',
                borderTopColor: 'var(--semantic-content-brand)',
                animation: 'data-overview-spin 0.8s linear infinite',
            }}
        />
        <div style={{ marginTop: 16, color: 'var(--semantic-content-brand)' }}>Analysing data</div>
    </div>
);

/** Compact per-box empty state — text + "+" to add data for that side. */
const AnalysisBoxEmpty = ({ usecase, isBenchmark }) => (
    <div className="data-overview-analysis-box__empty fs-unmask">
        <Text as="p" variant="base-regular" color="secondary" style={{ margin: 0 }}>
            Nothing here yet
        </Text>
        {usecase ? (
            <AddDataButton
                usecase={usecase}
                iconOnly
                uploadTarget={isBenchmark ? 'benchmark' : 'dataset'}
                placement="bottom"
            />
        ) : null}
    </div>
);

/**
 * One analysis box (Figma 3001:68774 / 3001:68826) — general section
 * (# of Examples, languages, code, tools called for agentic tasks, insights)
 * stacked above the "Output Detected" section (labels/entities pills + insights).
 */
const AnalysisBox = ({
    usecase,
    data,
    isBenchmark,
    loading,
    usable,
    onCodeChange,
    onLanguageChange,
    onPunctuationChange,
}) => {
    const taskType = usecase?.task_type;
    const hasOutputSection = showOutputPanel(taskType);

    // "Your data looks good" only earns its place when neither section has any
    // findings — otherwise an Output-side warning (e.g. "Labels seem to be
    // unbalanced") would be visually contradicted by an "all clear" message
    // sitting above it in the same box.
    const hasOutputInsights = hasOutputSection && (data?.outputInsights?.length || 0) > 0;

    let body;
    if (loading) {
        body = <LoadingState />;
    } else if (!usable) {
        body = <AnalysisBoxEmpty usecase={usecase} isBenchmark={isBenchmark} />;
    } else {
        body = (
            <>
                <div className="data-overview-analysis-section">
                    <MetricsRow examples={data?.metrics?.examples ?? data?.totalExamples ?? 0} />
                    <LanguageSection
                        languages={data?.languages}
                        significantLanguages={data?.significantLanguages}
                        totalExamples={data?.totalExamples}
                        onLanguageChange={onLanguageChange}
                        currentUsecase={usecase}
                        isBenchmark={isBenchmark}
                    />
                    <CodeSection
                        isCode={data?.isCode}
                        onCodeChange={onCodeChange}
                        currentUsecase={usecase}
                        isBenchmark={isBenchmark}
                    />
                    <ToolsCalledSection toolCounts={data?.toolCalls} usecase={usecase} />
                    <PunctuationSection
                        currentUsecase={usecase}
                        entitiesCounts={data?.entities}
                        selectedTaskType={taskType}
                        isPunctuationDetected={data?.isPunctuationDetected}
                        isBenchmark={isBenchmark}
                        onPunctuationChange={onPunctuationChange}
                    />
                    <InsightsSection
                        insights={data?.examplesInsights}
                        showDefaultWhenEmpty={!hasOutputInsights}
                    />
                </div>
                {hasOutputSection && (
                    <div className="data-overview-analysis-section data-overview-analysis-section--output">
                        <LabelsSection data={data} selectedTaskType={taskType} showHeader />
                        <InsightsSection
                            insights={data?.outputInsights}
                            showDefaultWhenEmpty={false}
                        />
                    </div>
                )}
            </>
        );
    }

    return <div className="data-overview-box data-overview-analysis-box">{body}</div>;
};

/**
 * Per-objective analysis row — train data and benchmark boxes side by side
 * (Figma 3001:68773), replacing the previous Train sets / Benchmarks tabs.
 */
const ObjectiveAnalysisBody = ({
    usecase,
    analysis,
    trainLoading,
    benchmarkLoading,
    trainUsable,
    benchmarkUsable,
    onCodeChange,
    onLanguageChange,
    onPunctuationChange,
}) => (
    <div className="data-overview-boxes-row data-overview-analysis-row">
        <AnalysisBox
            usecase={usecase}
            data={analysis?.data}
            isBenchmark={false}
            loading={trainLoading}
            usable={trainUsable}
            onCodeChange={onCodeChange}
            onLanguageChange={onLanguageChange}
            onPunctuationChange={onPunctuationChange}
        />
        <AnalysisBox
            usecase={usecase}
            data={analysis?.benchmarkData}
            isBenchmark
            loading={benchmarkLoading}
            usable={benchmarkUsable}
            onCodeChange={onCodeChange}
            onLanguageChange={onLanguageChange}
            onPunctuationChange={onPunctuationChange}
        />
    </div>
);

const withCodeStatus = (analysis, checked, isBenchmark) => {
    if (!analysis) return analysis;
    const dataKey = isBenchmark ? 'benchmarkData' : 'data';
    if (!analysis[dataKey]) return analysis;
    return {
        ...analysis,
        [dataKey]: {
            ...analysis[dataKey],
            isCode: checked,
        },
    };
};

/**
 * Unified Data Overview card (Figma 3001:68738).
 *
 * Single source of truth for the data overview shown on the task landing
 * page (`/task/:groupName/:taskName`). Renders a centered 1200px column with
 * the title block above one outer card containing side-by-side Datasets /
 * Benchmarks file boxes (plus External Files when present) and side-by-side
 * train / benchmark analysis boxes — each stacking "# of Examples",
 * language/code/insights above the per-objective "Output Detected" panel.
 * A side without data shows the dashed Upload Dataset / Upload Benchmark
 * empty card (Figma 3215:11841). A task without any data shows both cards
 * side by side.
 *
 * @param {Object} props
 * @param {Object} [props.subGroup] Task sub-group with `usecases[]`. Required when there are multiple objectives.
 * @param {Object} [props.currentUsecase] Fallback usecase when not in a sub-group (single-objective tasks).
 * @param {string} [props.activeUsecaseId] Controlled active objective id.
 * @param {(usecaseId: string) => void} [props.onActiveUsecaseChange] Called when the user clicks a different objective tab.
 * @param {(checked: boolean, isBenchmark: boolean) => void} [props.onCodeChange]
 * @param {(lang: string, checked: boolean, isBenchmark: boolean) => void} [props.onLanguageChange]
 * @param {(checked: boolean, isBenchmark: boolean) => void} [props.onPunctuationChange]
 * @param {Object<string, {data, benchmarkData, externalModelCounts, datasetsInfo, isLoading}>} [props.analysisOverrides]
 *   Pre-fetched analysis keyed by usecase id (used by hosts that already have analysis loaded — avoids duplicating the per-objective fetch).
 * @param {boolean} [props.isAnalysisLoading=false] Loading flag forwarded from the host page (e.g. cleanup-in-progress).
 * @param {number} [props.dataOverviewRefreshKey=0] Bumped by the host when data-editor
 *   mutations change labels so analysis probes refetch (train/benchmark insights).
 */
const DataOverviewCard = ({
    subGroup,
    currentUsecase,
    activeUsecaseId,
    onActiveUsecaseChange,
    onCodeChange,
    onLanguageChange,
    onPunctuationChange,
    analysisOverrides,
    isAnalysisLoading = false,
    dataOverviewRefreshKey = 0,
}) => {
    const usecases = useMemo(() => {
        if (subGroup?.usecases?.length) return subGroup.usecases;
        if (currentUsecase) return [currentUsecase];
        return [];
    }, [subGroup, currentUsecase]);

    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const { confirmRemoval, removeFromUsecase } = useUsecaseFileRemoval();
    const { progressStatus, cancelGeneration } = useContext(GlobalContext);

    /**
     * Detach a dataset/benchmark file from every usecase in this card. The
     * file may belong to several objectives in a multi-objective sub-group, so we
     * fan-out the same `removeFromUsecase` call we use from `FromLibraryTab`
     * to each owning usecase, then let the hook refresh once at the end.
     * Skipped silently for Viewers and for `manual` files (those have their
     * own removal flow via `ExternalModelResultsSelect`).
     *
     * Multi-objective uploads register a different per-subtask clone name
     * on each sibling usecase but the tile collapses them into a single
     * raw display name. We carry every underlying clone in ``cloneNames``
     * and tell each owning usecase to remove just the clone(s) it actually
     * has, so the user sees one tile but the backend cleanly detaches every
     * sibling's slice in one click.
     */
    const handleRemoveFile = useCallback(
        (file) => {
            if (role === 'Viewer') return;
            const isBenchmark = file.kind === 'benchmark';
            const isDataset = file.kind === 'dataset';
            if (!isBenchmark && !isDataset) return;
            const cloneNames = file.cloneNames?.length ? file.cloneNames : [file.name];
            const removalPlan = usecases
                .map((uc) => {
                    const ownedNames = ((isBenchmark ? uc.benchmarks : uc.datasets) || []).filter(
                        (n) => cloneNames.includes(n)
                    );
                    return ownedNames.length ? { usecase: uc, ownedNames } : null;
                })
                .filter(Boolean);
            if (!removalPlan.length) return;
            const sdkCloneName = cloneNames.find(isSdkTraceDatasetName);
            const exportContext = sdkCloneName
                ? {
                    usecase: removalPlan[0].usecase,
                    datasetName: sdkCloneName,
                    isBenchmark,
                }
                : null;
            confirmRemoval({
                names: [file.name],
                isBenchmark,
                exportContext,
                onConfirm: () => {
                    (file.usecaseIds || []).forEach((usecaseId) => {
                        const entry = listActiveAiGenerationEntriesForUsecase(
                            progressStatus,
                            usecaseId
                        ).find((candidate) => fileMatchesAiGenerationDataset(file, candidate.datasetName));
                        if (entry) {
                            cancelGeneration?.(
                                entry.progressKey || aiGenerationProgressKey(usecaseId, entry.datasetName)
                            );
                        }
                    });
                    return Promise.all(
                        removalPlan.map(({ usecase, ownedNames }) =>
                            removeFromUsecase({
                                usecase,
                                namesToRemove: ownedNames,
                                isBenchmark,
                            })
                        )
                    );
                },
            });
        },
        [usecases, role, confirmRemoval, removeFromUsecase, progressStatus, cancelGeneration]
    );

    const onRemoveFile = role === 'Viewer' ? undefined : handleRemoveFile;

    const [internalActiveId, setInternalActiveId] = useState(
        activeUsecaseId || usecases[0]?._id || null
    );

    useEffect(() => {
        if (activeUsecaseId && activeUsecaseId !== internalActiveId) {
            setInternalActiveId(activeUsecaseId);
        }
    }, [activeUsecaseId, internalActiveId]);

    useEffect(() => {
        if (!internalActiveId && usecases[0]?._id) {
            setInternalActiveId(usecases[0]._id);
        }
    }, [usecases, internalActiveId]);

    const effectiveActiveId = internalActiveId || usecases[0]?._id || null;

    const handleActiveChange = useCallback(
        (usecaseId) => {
            setInternalActiveId(usecaseId);
            onActiveUsecaseChange?.(usecaseId);
        },
        [onActiveUsecaseChange]
    );

    const [analysisMap, setAnalysisMap] = useState({});

    const handleProbeResult = useCallback((usecaseId, result) => {
        setAnalysisMap((prev) => {
            const existing = prev[usecaseId];
            if (
                existing &&
                existing.data === result.data &&
                existing.benchmarkData === result.benchmarkData &&
                existing.externalModelCounts === result.externalModelCounts &&
                existing.datasetsInfo === result.datasetsInfo &&
                existing.isLoading === result.isLoading
            ) {
                return prev;
            }
            return { ...prev, [usecaseId]: result };
        });
    }, []);

    const probeUsecases = useMemo(
        () => usecases.filter((uc) => !analysisOverrides?.[uc._id]),
        [usecases, analysisOverrides]
    );

    /**
     * Names of files currently mid-upload or mid-processing.
     *
     * The data_processor publishes `updateProgress` WS events that land in
     * `progressStatus[file_name]`; while the entry exists with
     * `progress < 100` and is neither completed nor errored, the dataset_info
     * doc only carries partial stats (often zero examples). Renders that show
     * the analysis tab at this point would lie about the row count, so we
     * collapse the body to the spinner instead.
     */
    const inFlightFileNames = useMemo(
        () => collectInFlightProgressFileNames(progressStatus),
        [progressStatus]
    );

    const usecaseHasInFlightUpload = useCallback(
        (uc) => {
            if (!uc) return false;
            if (isAiGenerationInFlightForUsecase(progressStatus, uc._id)) return true;
            if (!inFlightFileNames.size) return false;
            const fileNames = [
                ...(uc.datasets || []),
                ...(uc.benchmarks || []),
                ...(uc.manual_model_predictions || []),
            ];
            // For multi-objective uploads ``progressStatus`` is keyed by the
            // parent group (raw filename) while ``uc.datasets`` carries the
            // per-subtask clone (``…__t_<6chars>``). Match against both so the
            // loading state stays accurate during composed-task ingestion.
            return fileNames.some(
                (n) => inFlightFileNames.has(n) || inFlightFileNames.has(stripCloneSuffix(n))
            );
        },
        [inFlightFileNames, progressStatus],
    );

    const usecaseHasInFlightDatasetUpload = useCallback(
        (uc) => {
            if (!uc) return false;
            if (isAiGenerationInFlightForUsecase(progressStatus, uc._id)) return true;
            if (!inFlightFileNames.size) return false;
            const fileNames = uc.datasets || [];
            return fileNames.some(
                (n) => inFlightFileNames.has(n) || inFlightFileNames.has(stripCloneSuffix(n))
            );
        },
        [inFlightFileNames, progressStatus],
    );

    const usecaseHasInFlightBenchmarkUpload = useCallback(
        (uc) => {
            if (!uc || !inFlightFileNames.size) return false;
            const fileNames = uc.benchmarks || [];
            return fileNames.some(
                (n) => inFlightFileNames.has(n) || inFlightFileNames.has(stripCloneSuffix(n))
            );
        },
        [inFlightFileNames],
    );

    const usecaseHasInFlightManualUpload = useCallback(
        (uc) => {
            if (!uc || !inFlightFileNames.size) return false;
            const fileNames = uc.manual_model_predictions || [];
            return fileNames.some(
                (n) => inFlightFileNames.has(n) || inFlightFileNames.has(stripCloneSuffix(n))
            );
        },
        [inFlightFileNames],
    );

    /**
     * Resolve a per-tile upload indicator (e.g. "Uploading…" / "Processing…")
     * from `progressStatus`. While a file is mid-upload the dataset_info doc
     * has no size yet, so without this the tile would render a misleading
     * "0 B" sub-label. Returns null when nothing is in flight for the tile,
     * letting the caller fall back to the formatted size.
     *
     * `progressStatus` is keyed by the parent (raw) filename — the same
     * display name we surface on the tile — and for multi-objective uploads
     * we also try the stripped clone name so each tile lights up while the
     * shared upload is still running.
     */
    const getUploadStatus = useCallback(
        (file) => resolveFileTileProgressStatus(file, progressStatus),
        [progressStatus],
    );

    /**
     * Per-usecase refetch trigger keyed by completion timestamps.
     *
     * Probes are fed `refreshKey` so they pull a fresh snapshot the moment
     * an attached file finishes processing. Without this the first-time
     * probe (issued the instant the file is added to the usecase) would
     * cache a partial dataset_info doc forever and the card would never
     * show the final counts even after data_processor was done.
     */
    const refreshKeysRef = useRef({});
    const prevInFlightRef = useRef(new Set());
    const [refreshKeysVersion, setRefreshKeysVersion] = useState(0);

    useEffect(() => {
        const prev = prevInFlightRef.current;
        const justFinished = new Set();
        prev.forEach((name) => {
            if (!inFlightFileNames.has(name)) justFinished.add(name);
        });
        prevInFlightRef.current = inFlightFileNames;

        if (!justFinished.size) return;

        let mutated = false;
        usecases.forEach((uc) => {
            const fileNames = [
                ...(uc.datasets || []),
                ...(uc.benchmarks || []),
                ...(uc.manual_model_predictions || []),
            ];
            if (
                !fileNames.some(
                    (n) => justFinished.has(n) || justFinished.has(stripCloneSuffix(n))
                )
            ) {
                return;
            }
            refreshKeysRef.current[uc._id] = (refreshKeysRef.current[uc._id] || 0) + 1;
            mutated = true;
        });
        if (mutated) {
            setRefreshKeysVersion((v) => v + 1);
        }
    }, [inFlightFileNames, usecases]);
    void refreshKeysVersion;

    const mergedAnalysisMap = useMemo(() => {
        const merged = { ...analysisMap };
        for (const uc of usecases) {
            const override = analysisOverrides?.[uc._id];
            if (override) merged[uc._id] = override;
        }
        return merged;
    }, [analysisMap, analysisOverrides, usecases]);

    const aggregatedFiles = useMemo(() => {
        const seen = new Map();
        const recordTile = ({
            kind,
            displayName,
            actualName,
            usecase,
            size,
            sizeLoading,
            sourceLoading,
            origin,
            sourceConfig,
            createdByUser,
            createdDatetime,
        }) => {
            const key = `${kind}:${displayName}`;
            const existing = seen.get(key);
            if (existing) {
                if (!existing.cloneNames.includes(actualName)) {
                    existing.cloneNames.push(actualName);
                }
                if (!existing.usecaseIds.includes(usecase._id)) {
                    existing.usecaseIds.push(usecase._id);
                }
                if (!existing.size && size) {
                    existing.size = size;
                }
                existing.sizeLoading =
                    !existing.size && (existing.sizeLoading || Boolean(sizeLoading));
                if (!existing.origin && origin) {
                    existing.origin = origin;
                }
                if (!existing.sourceConfig && sourceConfig) {
                    existing.sourceConfig = sourceConfig;
                }
                existing.sourceLoading =
                    !existing.sourceConfig
                    && (existing.sourceLoading || Boolean(sourceLoading));
                if (!existing.createdByUser && createdByUser) {
                    existing.createdByUser = createdByUser;
                }
                if (!existing.createdDatetime && createdDatetime) {
                    existing.createdDatetime = createdDatetime;
                }
                return;
            }
            seen.set(key, {
                kind,
                name: displayName,
                rawName: displayName,
                cloneNames: [actualName],
                usecaseIds: [usecase._id],
                size: size || 0,
                sizeLoading: !size && Boolean(sizeLoading),
                sourceLoading: !sourceConfig && Boolean(sourceLoading),
                origin,
                sourceConfig,
                createdByUser,
                createdDatetime,
            });
        };

        usecases.forEach((uc) => {
            const ucAnalysis = mergedAnalysisMap[uc._id];
            // While the analysis probe hasn't reported (or is fetching) the
            // dataset_info docs, a zero size means "unknown", not "0 B" — the
            // tile shows a spinner in place of the size until it resolves.
            const analysisLoading = !ucAnalysis || ucAnalysis.isLoading;
            const ucDatasetsInfo = ucAnalysis?.datasetsInfo || [];
            const infoByName = new Map();
            ucDatasetsInfo.forEach((info) => {
                if (info?.dataset) {
                    infoByName.set(info.dataset, info);
                }
            });

            (uc.datasets || []).forEach((name) => {
                const info = infoByName.get(name);
                const size = info?.request_example_summary_data?.num_characters?.sum || 0;
                recordTile({
                    kind: 'dataset',
                    displayName: getDataOverviewFileDisplayName(name),
                    actualName: name,
                    usecase: uc,
                    size,
                    sizeLoading: analysisLoading && !size,
                    sourceLoading: analysisLoading,
                    origin: info?.data_creation_type,
                    ...sourceFieldsFromDatasetInfoDoc(info),
                });
            });

            (uc.benchmarks || []).forEach((name) => {
                const info = infoByName.get(name);
                const size = info?.request_example_summary_data?.num_characters?.sum || 0;
                recordTile({
                    kind: 'benchmark',
                    displayName: getDataOverviewFileDisplayName(name),
                    actualName: name,
                    usecase: uc,
                    size,
                    sizeLoading: analysisLoading && !size,
                    sourceLoading: analysisLoading,
                    origin: info?.data_creation_type,
                    ...sourceFieldsFromDatasetInfoDoc(info),
                });
            });

            (uc.manual_model_predictions || []).forEach((name) => {
                const info = infoByName.get(name);
                const size = info?.request_example_summary_data?.num_characters?.sum || 0;
                recordTile({
                    kind: 'manual',
                    displayName: getDataOverviewFileDisplayName(name),
                    actualName: name,
                    usecase: uc,
                    size,
                    sizeLoading: analysisLoading && !size,
                    origin: undefined,
                    sourceConfig: undefined,
                    createdByUser: undefined,
                    createdDatetime: undefined,
                });
            });
        });
        return Array.from(seen.values());
    }, [usecases, mergedAnalysisMap]);

    const hasAnyDatasets = useMemo(
        () => usecases.some((uc) => mergedAnalysisMap[uc._id]?.data?.totalExamples),
        [usecases, mergedAnalysisMap]
    );
    const hasAnyBenchmarks = useMemo(
        () => usecases.some((uc) => mergedAnalysisMap[uc._id]?.benchmarkData?.totalExamples),
        [usecases, mergedAnalysisMap]
    );

    const hasUsecaseFileRefs = useMemo(
        () => usecases.some(
            (uc) =>
                (uc.datasets || []).length > 0
                || (uc.benchmarks || []).length > 0
                || (uc.manual_model_predictions || []).length > 0
        ),
        [usecases]
    );

    const hasDatasetFiles = useMemo(
        () => aggregatedFiles.some((f) => f.kind === 'dataset'),
        [aggregatedFiles]
    );
    const hasBenchmarkFiles = useMemo(
        () => aggregatedFiles.some((f) => f.kind === 'benchmark'),
        [aggregatedFiles]
    );

    /** Either attached files or analysis with rows — avoids blanking a side until both report counts */
    const trainSideUsable = hasDatasetFiles || hasAnyDatasets;
    const benchmarkSideUsable = hasBenchmarkFiles || hasAnyBenchmarks;

    const handleCodeChange = useCallback(
        (checked, isBenchmarkValue) => {
            setAnalysisMap((prev) => {
                let changed = false;
                const next = { ...prev };
                usecases.forEach((uc) => {
                    const updated = withCodeStatus(next[uc._id], checked, isBenchmarkValue);
                    if (updated !== next[uc._id]) {
                        next[uc._id] = updated;
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
            onCodeChange?.(checked, isBenchmarkValue);
        },
        [usecases, onCodeChange]
    );

    const activeUsecase = useMemo(
        () => usecases.find((u) => u._id === effectiveActiveId) || usecases[0] || null,
        [usecases, effectiveActiveId]
    );

    const activeAnalysis = activeUsecase
        ? mergedAnalysisMap[activeUsecase._id]
        : null;

    const activeUsecaseHasInFlightUpload = activeUsecase
        ? usecaseHasInFlightUpload(activeUsecase)
        : false;

    const activeInFlightDataset = activeUsecase
        ? usecaseHasInFlightDatasetUpload(activeUsecase)
        : false;
    const activeInFlightBenchmark = activeUsecase
        ? usecaseHasInFlightBenchmarkUpload(activeUsecase)
        : false;
    const activeInFlightManual = activeUsecase
        ? usecaseHasInFlightManualUpload(activeUsecase)
        : false;

    const activeHasDatasetFiles = Boolean((activeUsecase?.datasets || []).length);
    const activeHasBenchmarkFiles = Boolean((activeUsecase?.benchmarks || []).length);

    const isLoadingActive =
        isAnalysisLoading
        || activeUsecaseHasInFlightUpload
        || (activeAnalysis ? activeAnalysis.isLoading : true);

    /**
     * The train and benchmark boxes share one analysis fetch, but uploads and
     * ingestion are split: a dataset upload must not blank the benchmark box
     * with a spinner, and a benchmark-only upload must not block train data.
     */
    const analysisGateLoadingTrain =
        !activeAnalysis
            ? activeHasDatasetFiles || activeInFlightDataset
            : activeAnalysis.isLoading
            && !activeAnalysis.data
            && (activeHasDatasetFiles || activeInFlightDataset);

    const analysisGateLoadingBenchmark =
        !activeAnalysis
            ? activeHasBenchmarkFiles || activeInFlightBenchmark
            : activeAnalysis.isLoading
            && !activeAnalysis.benchmarkData
            && (activeHasBenchmarkFiles || activeInFlightBenchmark);

    const trainBodyLoading =
        isAnalysisLoading
        || activeInFlightManual
        || activeInFlightDataset
        || analysisGateLoadingTrain;

    const benchmarkBodyLoading =
        isAnalysisLoading
        || activeInFlightManual
        || activeInFlightBenchmark
        || analysisGateLoadingBenchmark;

    const showEmptyState =
        !aggregatedFiles.length
        && !hasAnyDatasets
        && !hasAnyBenchmarks
        && !inFlightFileNames.size
        && (!isLoadingActive || !hasUsecaseFileRefs);

    const { dispatchTaskDataEvent } = useTaskDataWindowEvents();
    const uploadActionsDisabled = readOnly || role === 'Viewer' || !activeUsecase;

    const openUploadWithFiles = useCallback(
        (files, uploadTarget) => {
            const fileList = Array.from(files || []).filter(Boolean);
            if (!activeUsecase || fileList.length === 0) return;
            dispatchTaskDataEvent(
                'task:add-data',
                {
                    source: 'upload',
                    usecaseId: activeUsecase._id,
                    uploadTarget,
                    files: fileList,
                    file: fileList[0],
                },
                activeUsecase
            );
        },
        [activeUsecase, dispatchTaskDataEvent]
    );

    const openLibrary = useCallback(
        (uploadTarget) => {
            if (!activeUsecase) return;
            dispatchTaskDataEvent(
                'task:add-data',
                {
                    source: 'library',
                    usecaseId: activeUsecase._id,
                    uploadTarget,
                },
                activeUsecase
            );
        },
        [activeUsecase, dispatchTaskDataEvent]
    );

    const renderUploadEmptyCard = useCallback(
        (target) => (
            <DataOverviewUploadEmptyCard
                target={target}
                taskType={activeUsecase?.task_type}
                isAgentic={isAgenticAutonomyLevel(activeUsecase?.autonomy_level)}
                disabled={uploadActionsDisabled}
                onUploadFiles={(files) => openUploadWithFiles(files, target)}
                onOpenLibrary={() => openLibrary(target)}
            />
        ),
        [activeUsecase?.task_type, uploadActionsDisabled, openUploadWithFiles, openLibrary]
    );

    const renderObjectivePanel = useCallback(
        (uc, { trainUsable = trainSideUsable, benchmarkUsable = benchmarkSideUsable } = {}) => {
            const analysis = mergedAnalysisMap[uc._id];
            // One-sided layouts render a single AnalysisBox; both-usable keeps
            // the side-by-side train / benchmark pair.
            if (trainUsable && !benchmarkUsable) {
                return (
                    <AnalysisBox
                        usecase={uc}
                        data={analysis?.data}
                        isBenchmark={false}
                        loading={trainBodyLoading}
                        usable
                        onCodeChange={handleCodeChange}
                        onLanguageChange={onLanguageChange}
                        onPunctuationChange={onPunctuationChange}
                    />
                );
            }
            if (!trainUsable && benchmarkUsable) {
                return (
                    <AnalysisBox
                        usecase={uc}
                        data={analysis?.benchmarkData}
                        isBenchmark
                        loading={benchmarkBodyLoading}
                        usable
                        onCodeChange={handleCodeChange}
                        onLanguageChange={onLanguageChange}
                        onPunctuationChange={onPunctuationChange}
                    />
                );
            }
            return (
                <ObjectiveAnalysisBody
                    usecase={uc}
                    analysis={analysis}
                    trainLoading={trainBodyLoading}
                    benchmarkLoading={benchmarkBodyLoading}
                    trainUsable={trainUsable}
                    benchmarkUsable={benchmarkUsable}
                    onCodeChange={handleCodeChange}
                    onLanguageChange={onLanguageChange}
                    onPunctuationChange={onPunctuationChange}
                />
            );
        },
        [
            mergedAnalysisMap,
            trainBodyLoading,
            benchmarkBodyLoading,
            trainSideUsable,
            benchmarkSideUsable,
            handleCodeChange,
            onLanguageChange,
            onPunctuationChange,
        ]
    );

    const renderAnalysisForSide = useCallback(
        (side) => {
            if (!activeUsecase) return null;
            const trainUsable = side === 'dataset';
            const benchmarkUsable = side === 'benchmark';
            if (usecases.length > 1) {
                return (
                    <ObjectiveTabs
                        usecases={usecases}
                        activeUsecaseId={effectiveActiveId}
                        onChange={handleActiveChange}
                        renderPanel={(uc) =>
                            renderObjectivePanel(uc, { trainUsable, benchmarkUsable })
                        }
                        scrollable
                    />
                );
            }
            return renderObjectivePanel(activeUsecase, { trainUsable, benchmarkUsable });
        },
        [
            activeUsecase,
            usecases,
            effectiveActiveId,
            handleActiveChange,
            renderObjectivePanel,
        ]
    );

    const bothSidesUsable = trainSideUsable && benchmarkSideUsable;
    // Tall upload empty only when exactly one side has data (Figma 3215:11488).
    const oneSideEmpty =
        !showEmptyState && !bothSidesUsable && (trainSideUsable || benchmarkSideUsable);

    return (
        <div className="data-overview">
            {probeUsecases.map((uc) => (
                <UsecaseAnalysisProbe
                    key={uc._id}
                    usecase={uc}
                    onResult={handleProbeResult}
                    refreshKey={
                        dataOverviewRefreshKey + (refreshKeysRef.current[uc._id] || 0)
                    }
                />
            ))}

            <div className="data-overview__intro fs-unmask">
                <Text
                    as="p"
                    variant="heading-5-semibold"
                    color="primary"
                    className="data-overview__intro-title"
                >
                    Data Overview
                </Text>
                <Text
                    as="p"
                    variant="base-regular"
                    color="secondary"
                    className="data-overview__intro-subtitle"
                >
                    The following properties were detected. You can adjust and improve this data in
                    the data editor.
                </Text>
            </div>

            <div className="data-overview-card">
                <style>
                    {'@keyframes data-overview-spin { to { transform: rotate(360deg); } }'}
                </style>

                {showEmptyState ? (
                    <div className="data-overview-upload-empty-row fs-unmask">
                        {renderUploadEmptyCard('dataset')}
                        {renderUploadEmptyCard('benchmark')}
                    </div>
                ) : oneSideEmpty ? (
                    <div className="data-overview-main-grid fs-unmask">
                        {trainSideUsable ? (
                            <>
                                <div className="data-overview-main-grid__files data-overview-main-grid__files--dataset">
                                    <FilesBoxesRow
                                        files={aggregatedFiles.filter(
                                            (f) => f.kind === 'dataset' || f.kind === 'manual'
                                        )}
                                        onRemoveFile={onRemoveFile}
                                        getUploadStatus={getUploadStatus}
                                        progressStatus={progressStatus}
                                        addDataUsecase={activeUsecase}
                                        showBenchmarks={false}
                                        showDatasets
                                    />
                                </div>
                                <div className="data-overview-main-grid__analysis data-overview-main-grid__analysis--dataset">
                                    {renderAnalysisForSide('dataset')}
                                </div>
                            </>
                        ) : (
                            <div className="data-overview-main-grid__empty data-overview-main-grid__empty--dataset">
                                {renderUploadEmptyCard('dataset')}
                            </div>
                        )}
                        {benchmarkSideUsable ? (
                            <>
                                <div className="data-overview-main-grid__files data-overview-main-grid__files--benchmark">
                                    <FilesBoxesRow
                                        files={aggregatedFiles.filter(
                                            (f) => f.kind === 'benchmark' || f.kind === 'manual'
                                        )}
                                        onRemoveFile={onRemoveFile}
                                        getUploadStatus={getUploadStatus}
                                        progressStatus={progressStatus}
                                        addDataUsecase={activeUsecase}
                                        showDatasets={false}
                                        showBenchmarks
                                    />
                                </div>
                                <div className="data-overview-main-grid__analysis data-overview-main-grid__analysis--benchmark">
                                    {renderAnalysisForSide('benchmark')}
                                </div>
                            </>
                        ) : (
                            <div className="data-overview-main-grid__empty data-overview-main-grid__empty--benchmark">
                                {renderUploadEmptyCard('benchmark')}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <FilesBoxesRow
                            files={aggregatedFiles}
                            onRemoveFile={onRemoveFile}
                            getUploadStatus={getUploadStatus}
                            progressStatus={progressStatus}
                            addDataUsecase={activeUsecase}
                        />
                        {usecases.length > 1 ? (
                            <ObjectiveTabs
                                usecases={usecases}
                                activeUsecaseId={effectiveActiveId}
                                onChange={handleActiveChange}
                                renderPanel={(uc) => renderObjectivePanel(uc)}
                                scrollable
                            />
                        ) : activeUsecase ? (
                            renderObjectivePanel(activeUsecase)
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

// Exposed for section-ordering tests; the card itself is the public API.
export { AnalysisBox };

export default DataOverviewCard;
